import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  Grid,
  LuxButton,
  LuxTabsGroup,
  LuxTimeSlot,
  LuxTypography,
  LuxLoader,
  LuxSelectablePill,
  useTheme,
  useMediaQuery,
} from '@gemini/fe-ds-core';
import { format, useEvent } from '@gemini/fe-utils';
import useGetHourSlots from '@hooks/useGetHourSlots';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CalendarSection, CalendarOverlay } from './CalendarPage.styles';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import {
  ChooseNewSlotType,
  InsuranceInfo,
  Patient,
  usePatientStore,
} from '@store/patientStore';
import useHourSlotMutation from '@hooks/useHourSlotMutation';
import useDeleteHourSlot from '@hooks/useDeleteHourSlot';
import { HourSlot, Slot } from '@utils/interfaces';
import { FormIds } from '@utils/enums/FormIds.enum';
import {
  StepContainer,
  // NoWhiteSpaceLuxSelectablePill,
} from '@components/Steps/Steps.styles';
import MoreInformationModal from '@components/MoreInformationModal/MoreInformationModal';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import MultiplePatientsTabs from '@components/MultiplePatietnsTabs/MultiplePatientsTabs';
import { cloneObject, deepEqual } from '@utils/functions';
import { CalendarComponent } from './CalendarComponent';
import useReservationRefresh from '@hooks/useReservationRefresh';
import {
  IChangeEvent,
  ICheckEvent,
  IScheduleExamContinueAttemptEvent,
  OBEvents,
} from '@utils/events/events';
import { Helmet } from 'react-helmet';
import MultipatientsTags from './MultipatientsTags';
import useAnalytics, { AnalyticsActionType } from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { BrandNameList } from '@utils/consts/brandNameList';
import dayjs from 'dayjs';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import CalendarInsuranceForm from '@components/InsurancesForm/CalendarInsuranceForm/CalendarInsuranceForm.component';
import { CalendarInsuranceFormData } from '@components/InsurancesForm/CalendarInsuranceForm/CalendarInsuranceForm.interface';
import {
  getCalendarInsuranceSelectedForAvailability,
  normalizeCalendarInsuranceFormData,
} from '@components/InsurancesForm/CalendarInsuranceForm/CalendarInsuranceForm.utils';
import { createHourSlotGroups, sortHourSlots } from './CalendarPage.utils';
export const HOURTEMPLATE = 'h:mm A';

//previous slot in reschedule flow. It needs to be seen disabled when we land on reschedule schedule exam step
interface SlotFromRescheduleFlow {
  doctor: string;
  day: string;
  hour: string;
}

export interface CalendarProps {
  setEnableButton: (val: boolean) => void;
  previousSlotToDisable?: SlotFromRescheduleFlow;
}

export enum STATUS_ALERT {
  NO_ACTION,
  NEED_TO_DELETE,
  GET_DAYS_TO_VERIFY,
  HAPPY_FLOW,
  DAY_AVAILABLE,
  DAY_NOT_AVAILABLE,
  SLOT_AVAILABLE,
  SLOT_NOT_AVAILABLE,
}

const getHourSlotId = (slot?: HourSlot) =>
  slot?.slotId || slot?.internalSlots?.[0]?.slotId;

const isSameHourSlot = (first?: HourSlot, second?: HourSlot) => {
  if (!first || !second) return false;

  const firstSlotId = getHourSlotId(first);
  const secondSlotId = getHourSlotId(second);

  if (firstSlotId && secondSlotId && firstSlotId === secondSlotId) {
    return true;
  }

  return (
    first.startTime === second.startTime &&
    first.doctorId === second.doctorId &&
    first.doctorName === second.doctorName
  );
};

// Resolve the UI time tab from the selected slot start time, independently from the stored timeTab.
const getTimeTabIndexBySlotStartTime = (slot: HourSlot) => {
  const slotStart = dayjs(slot.startTime);
  const slotMinutes = slotStart.hour() * 60 + slotStart.minute();

  // Same tab intervals documented for the previous slot insertion:
  // Tab 1: 08:00 - 11:59
  // Tab 2: 12:00 - 15:59
  // Tab 3: 16:00 - 20:00
  if (slotMinutes >= 8 * 60 && slotMinutes <= 11 * 60 + 59) return 0;
  if (slotMinutes >= 12 * 60 && slotMinutes <= 15 * 60 + 59) return 1;
  if (slotMinutes >= 16 * 60 && slotMinutes <= 20 * 60) return 2;

  return -1;
};

// Keep the selected slot in a single tab: remove duplicates first, then insert it in the resolved tab.
const addSelectedSlotToTimeTab = (
  tempSlots: Array<Slot>,
  selectedSlot: HourSlot,
  timeTabIndex: number,
) => {
  if (!tempSlots[timeTabIndex]) return;

  tempSlots.forEach(slotGroup => {
    slotGroup.list = slotGroup.list.filter(
      slot => !isSameHourSlot(slot, selectedSlot),
    );
  });

  tempSlots[timeTabIndex].list.push(selectedSlot);
};

/**
 * CalenddarPage
 * - Firstime verify if the user changed appt type or insurance
 * - If true
 *  - Delete prev reservation
 *  - get days available and Verify if day pre selected is available
 *    - If true
 *      - Verify if start time of prev slot is available
 *        - If true
 *          - Reserve new slot and if response is ok not show any banner
 *        - If false
 *          - Show banner with error
 *    - If false
 *      - Show banner with error
 *      - Get list slot available and select the time tab automatically
 * - If false
 *  - get days available, get list slot available and selecet the time tab automatically
 * @param param0
 * @returns
 */
const CalendarPage = ({
  setEnableButton,
  previousSlotToDisable,
}: CalendarProps) => {
  const { t } = useTranslation();
  const { emit, subscribe } = useEvent();
  const theme = useTheme();
  const { trackPageAnalytics, trackEventAnalytics } = useAnalytics();
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);

  //Patient Store
  const getPatients = usePatientStore(state => state.getPatients);
  const patients = getPatients();
  const getCurrentPatient = usePatientStore(state => state.getCurrentPatient);
  const updatePatient = usePatientStore(state => state.updatePatient);
  const setIsModified = usePatientStore(state => state.setIsModified);
  const resetScheduleExam = usePatientStore(state => state.resetScheduleExam);
  const setStoreReservationId = usePatientStore(
    state => state.updateReservationId,
  );
  // save the timer slot
  const setTimer = usePatientStore(state => state.setTimerScheduleExam);
  // used for time slot selection
  const setTimeSlotOrder = usePatientStore(
    state => state.setTimeTabScheduleExam,
  );
  // used for choose new slot (type, ruleUnit, ruleTime)
  const setChooseNewSlot = usePatientStore(
    state => state.setChooseNewSlotScheduleExam,
  );

  //OB store
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const providerId = useOnlineBookingStore(state => state.providerIdFromUrl);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);

  const generalData = useOnlineBookingStore(state => state.generalInfo);
  const generalConfig = useOnlineBookingStore(state => state.generalConfig);
  const showDoctors = useOnlineBookingStore(
    state => state.scheduleConfig?.showDoctorDetailsForWebScheduling,
  );
  const splitTimeSlot = useOnlineBookingStore(
    state => state.scheduleConfig?.splitTimeSlot ?? false,
  );
  const showYourSelection = useOnlineBookingStore(
    state => state.generalConfig?.customization?.showYourSelection ?? true,
  );
  const numColumnsTimeSlot = useOnlineBookingStore(
    state => state.generalConfig?.customization?.numColumnsTimeSlot ?? 4,
  );

  const patientId = useOnlineBookingStore(state => state.infoUser?.patientId);
  const prefillablePatientId =
    currentPatientIndex === 0 ? patientId : undefined;
  const showInsuranceInSchedule = useMemo(
    () =>
      !!formConfigs?.askForInsuranceInformation &&
      formConfigs?.insuranceMustDriveAvailability === true,
    [formConfigs],
  );

  const {
    data: insurances,
    isLoading: isInsurancesLoading,
    isError: isErrorInInsurances,
  } = useGetInsuranceConfiguration({
    organizationId: showInsuranceInSchedule
      ? generalData?.organizationId
      : undefined,
    patientId: prefillablePatientId,
  });

  const eyeExamWithUsAnswer = useMemo(
    () =>
      patients[currentPatientIndex]?.appointmentType.eyeExamWithUsAnswer ??
      false,
    [patients, currentPatientIndex],
  );

  //if we have a previousSlot, it means we are from reschedule flow
  const inRescheduleFlow = useMemo(
    () => !!previousSlotToDisable,
    [previousSlotToDisable],
  );

  // useState
  const preCurrentPatientIndex = useRef<number>(-1);
  const currentPatient: Patient | undefined = useMemo(() => {
    return patients && patients[currentPatientIndex];
  }, [patients, currentPatientIndex]);
  // more info modal Open
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);
  // default from store
  const [timeTab, setTimeTab] = useState<number | undefined>(
    currentPatient?.extraInfoScheduleExam?.timeTab,
  );
  // default from store
  const [selectedHourSlot, setSelectedHourSlot] = useState<
    HourSlot | undefined
  >(currentPatient?.scheduleExam?.slotId);
  const [scheduleExamSubmitAttemptCount, setScheduleExamSubmitAttemptCount] =
    useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    patients[currentPatientIndex]?.scheduleExam?.day,
  );
  const reservationId = useMemo(
    () => currentPatient?.reservationId || 'no-available', // we need to set a value because is using in the delete service
    [currentPatient],
  );
  const reservationIds = useMemo(
    () => patients.map(p => p?.reservationId || 'no-available'),
    [patients],
  );
  const [hourSlots, setHourSlots] = useState<Slot[]>();
  const [showSlotAlert, setShowSlotAlert] = useState(false);
  const [disabledSlots, setDisabledSlots] = useState<string[]>([]);
  const [reservationStatus, setReservationStatus] = useState<STATUS_ALERT>(
    STATUS_ALERT.NO_ACTION,
  );
  const [listEnableButton, setListEnableButton] = useState<
    Array<boolean | undefined>
  >(
    patients.map((p, index) => {
      if (!p) return undefined;
      if (index !== currentPatientIndex) {
        // Now we check if the other users have a reservationId and if the appointment or insurance remain unchanged.
        // It is ok also if the appointmentId is set, because it means that the user has already booked a slot with success.
        return (
          (!!p?.reservationId || !!p?.appointmentId) &&
          !p.appointmentType.isModified &&
          !p.insurance?.isModified
        );
      } else {
        return false;
      }
    }),
  );
  const [insuranceEnableButtonList, setInsuranceEnableButtonList] = useState<
    Array<boolean | undefined>
  >(
    patients.map(p =>
      p && showInsuranceInSchedule ? !!p.insurance?.plan : undefined,
    ),
  );
  const [calendarInsuranceValues, setCalendarInsuranceValues] = useState<
    Array<InsuranceInfo | undefined>
  >(patients.map(p => p?.insurance));

  const patientsPresenceKey = useMemo(
    () => patients.map(p => (p ? '1' : '0')).join('|'),
    [patients],
  );

  useEffect(() => {
    setCalendarInsuranceValues(prev =>
      patients.map((patient, index) =>
        patient ? prev[index] || patient.insurance : undefined,
      ),
    );
  }, [patients, patientsPresenceKey]);

  // Prevent initial flash of "not available days" before we actually queried slots.
  // This becomes true when the slots request starts or when slots data becomes available.
  const [hasQueriedSlots, setHasQueriedSlots] = useState(false);

  const appointmentType: string | undefined = useMemo(() => {
    return currentPatient?.appointmentType?.appointmentType;
  }, [currentPatient]);

  const currentCalendarInsurance = useMemo(
    () =>
      showInsuranceInSchedule
        ? calendarInsuranceValues[currentPatientIndex] ||
          currentPatient?.insurance
        : currentPatient?.insurance,
    [
      calendarInsuranceValues,
      currentPatient,
      currentPatientIndex,
      showInsuranceInSchedule,
    ],
  );

  const insuranceSelected: string | undefined = useMemo(() => {
    if (!showInsuranceInSchedule) return '';
    return getCalendarInsuranceSelectedForAvailability(
      currentCalendarInsurance,
    );
  }, [currentCalendarInsurance, showInsuranceInSchedule]);

  //ANALYTICS (RONA only) - Scheduler
  useEffect(() => {
    if (brandName) {
      trackPageAnalytics({
        page: FlowsPages.SCHEDULE_EXAM,
        patients,
      });
    }
  }, []);

  // console.log('#Calendar | listEnableButton: ', listEnableButton);
  // get the list of available slot
  const {
    data: slots, // -> hourSlots
    // refetch: getSlots,
    isFetching: getSlotsIsLoading,
  } = useGetHourSlots({
    practiceId: practiceId,
    appointmentType,
    insuranceSelected,
    previousExam: eyeExamWithUsAnswer,
    date: selectedDate ? format(selectedDate, 'YYYY-MM-DD') : undefined,
    reservationStatus,
    practitionerId: providerId,
  });

  // This effect exists to avoid the bug where for an instant we got the "no slots available" message
  // mark that we've started/attempted to query slots so we don't show the
  // "not available days" message on the very first render before the request.
  useEffect(() => {
    if (getSlotsIsLoading || slots !== undefined) {
      setHasQueriedSlots(true);
    }
  }, [getSlotsIsLoading, slots]);

  // reservation
  const {
    mutate: createReservationMutate,
    data: reservationData,
    isSuccess: isCreateReservationSuccess,
    isLoading: isCreateReservationLoading,
    isError,
    // isLoading,
  } = useHourSlotMutation({
    practiceId: practiceId,
  });

  //refresh reservation
  const { mutateAsync: refreshReservationOneMutate } = useReservationRefresh({
    practiceId: practiceId,
    reservationId: reservationIds[0],
  });
  const { mutateAsync: refreshReservationTwoMutate } = useReservationRefresh({
    practiceId: practiceId,
    reservationId: reservationIds[1],
  });
  const { mutateAsync: refreshReservationThreeMutate } = useReservationRefresh({
    practiceId: practiceId,
    reservationId: reservationIds[2],
  });

  // delete reservation
  const {
    mutateAsync: deleteReservationMutate,
    isSuccess: deleteReservationIsSuccess,
    isError: deleteReservationIsError,
    isLoading: isDeleteReservationLoading,
  } = useDeleteHourSlot({
    reservationId: reservationId,
  });

  //#region util functions
  // loading
  const isLoadingSlot = useMemo(
    () => isCreateReservationLoading || isDeleteReservationLoading,
    [isCreateReservationLoading, isDeleteReservationLoading],
  );
  const hasAvailableSlotToSelect = useMemo(
    () => !!hourSlots?.some(slotGroup => slotGroup.list.length > 0),
    [hourSlots],
  );
  const showRequiredSlotError = useMemo(
    () =>
      scheduleExamSubmitAttemptCount > 0 &&
      !selectedHourSlot &&
      !isLoadingSlot &&
      !getSlotsIsLoading &&
      hasAvailableSlotToSelect,
    [
      getSlotsIsLoading,
      hasAvailableSlotToSelect,
      isLoadingSlot,
      scheduleExamSubmitAttemptCount,
      selectedHourSlot,
    ],
  );
  // time tab
  const hourSlices = useMemo(() => {
    return [
      {
        index: 0,
        content: t('calendarPage.morning'),
      },
      {
        index: 1,
        content: t('calendarPage.afternoon'),
      },
      {
        index: 2,
        content: t('calendarPage.evening'),
      },
    ];
  }, [t]);
  // extra label
  const timeSlotLabels = [
    t('calendarPage.morning'),
    t('calendarPage.afternoon'),
    t('calendarPage.evening'),
  ];

  // Clickable phone text for "dontFindASlot?CallUs"
  const callUsClickable = useMemo(() => {
    const phoneRaw = generalData?.phoneNumber;
    if (!phoneRaw) return null;

    // sanitize phone for tel: (keep digits and leading +)
    const telNumber = phoneRaw.replace(/[^+\d]/g, '');
    const raw = t('calendarPage.dontFindASlot?CallUs');
    const placeholder = '{{phoneNumberPlaceholder}}';

    const phoneLink = (
      <a
        href={`tel:${telNumber}`}
        style={{
          textDecoration: 'underline',
          color: 'inherit',
          fontWeight: 700,
        }}
        aria-label={phoneRaw}
      >
        {phoneRaw}
      </a>
    );
    // If translation contains placeholder, render -> before + link + after
    const [before, after] = raw.split(placeholder);

    return (
      <LuxTypography
        fontSize={theme.palette.LUX.obTokens.textSmSize}
        fontFamily={theme.palette.LUX.obTokens.textSmFamily}
        style={{ whiteSpace: 'normal' }}
      >
        {before}
        {phoneLink}
        {after}
      </LuxTypography>
    );
  }, [t, generalData?.phoneNumber, theme]);

  const enableButtonMaster = useMemo(() => {
    const scheduleIsComplete = listEnableButton.every(
      enable => (typeof enable === 'boolean' && enable) || enable === undefined,
    );
    const insuranceIsComplete =
      !showInsuranceInSchedule ||
      insuranceEnableButtonList.every(
        enable =>
          (typeof enable === 'boolean' && enable) || enable === undefined,
      );

    return (
      scheduleIsComplete &&
      insuranceIsComplete &&
      !isLoadingSlot &&
      (!showInsuranceInSchedule || !isInsurancesLoading)
    );
  }, [
    insuranceEnableButtonList,
    isInsurancesLoading,
    isLoadingSlot,
    listEnableButton,
    showInsuranceInSchedule,
  ]);

  // build a date with a standard ISO format, valid and working for all browsers
  // input: day = '2023-10-31', hour = '2:30 PM' (as coming from BFF)
  // output: dayjs('2023-10-31T14:30:00') (the iso format)
  // (this is because trying to build a date with new Date('2023-10-31 2:30 PM') works only in some browsers)
  const buildISOLikeDate = (day: string, hour: string) => {
    const [time, mer] = hour.split(' ');
    const [h, m] = time.split(':').map(Number);
    const hours = (h % 12) + (mer?.toUpperCase() === 'PM' ? 12 : 0);
    const hh = hours.toString().padStart(2, '0');
    const mm = m.toString().padStart(2, '0');

    // ISO-like string
    const isoLikeDate = `${day}T${hh}:${mm}:00`;
    return { isoLikeDate, time24: `${hh}:${mm}` };
  };

  // Return true if slot in input corresponds to previousSlotToDisable
  const isPreviousSlotToDisable = (slot: HourSlot): boolean => {
    if (!previousSlotToDisable) return false;

    const { doctor, day, hour } = previousSlotToDisable;
    const { isoLikeDate: previousSlotDate } = buildISOLikeDate(day, hour);

    return slot.startTime === previousSlotDate && slot.doctorName === doctor;
  };

  const getHourSlotStatus = useCallback(
    (slot: HourSlot) => {
      // in reschedule flow: if this is the previousSlotToDisable
      // return status "success" (to show it green)
      if (previousSlotToDisable) {
        if (isPreviousSlotToDisable(slot)) {
          return 'success';
        }
      }

      const isCurrent =
        selectedHourSlot && isSameHourSlot(slot, selectedHourSlot);

      const slotId = getHourSlotId(slot);

      if ((slotId && disabledSlots.includes(slotId)) || slot.disabled) {
        return 'disabled';
      }

      if (isCurrent) {
        if (isError && !isLoadingSlot) {
          if (slotId) {
            setDisabledSlots(prevDisabledSlots => [
              ...prevDisabledSlots,
              slotId,
            ]);
          }
          return 'disabled';
        } else {
          return 'selected';
        }
      } else {
        return 'default';
      }
    },
    [selectedHourSlot, isError, isLoadingSlot, disabledSlots],
  );

  const setTimeTabFromSelectedSlot = (
    tempSlots: Array<Slot>,
    selectedHourSlot: HourSlot | undefined,
  ) => {
    if (!tempSlots || !selectedHourSlot) return;

    const group = tempSlots.find(slotGroup =>
      slotGroup.list.some(sl => isSameHourSlot(sl, selectedHourSlot)),
    );

    if (group) setTimeTab(group.timeSlotOrder);
  };

  const onTimeSlotClick = (slot: HourSlot) => {
    console.log('#Calendar | user click on new slot');
    setSelectedHourSlot(slot);
    if (reservationId !== 'no-available') {
      console.log(
        '#delete the prev reservationId and reset the timer: ',
        reservationId,
      );
      deleteReservationMutate(reservationId)
        .then(() => createReservationMutate(slot))
        .catch(() => createReservationMutate(slot)); //no check in this service.
      setTimer(currentPatientIndex, undefined);
    } else {
      createReservationMutate(slot);
    }

    patients.forEach((p, i) => {
      if (
        p?.reservationId &&
        reservationIds[i] !== 'no-available' &&
        currentPatientIndex !== i &&
        p?.scheduleExam?.slotId
      ) {
        switch (i) {
          case 0:
            refreshReservationOneMutate(p?.scheduleExam?.slotId).then(
              reservationData =>
                setStoreReservationId(i, reservationData.reservationId),
            );
            break;
          case 1:
            refreshReservationTwoMutate(p?.scheduleExam?.slotId).then(
              reservationData =>
                setStoreReservationId(i, reservationData.reservationId),
            );
            break;
          case 2:
            refreshReservationThreeMutate(p?.scheduleExam?.slotId).then(
              reservationData =>
                setStoreReservationId(i, reservationData.reservationId),
            );
            break;
          default:
            return;
        }
      }
    });

    //analytics event track
    trackEventAnalytics({
      action: AnalyticsActionType.ADD_PATIENT,
    });
  };

  const resetIsModified = useCallback(() => {
    // reset value isModified if the slot is available and it's taken
    if (
      currentPatient &&
      currentPatient.appointmentType?.isModified &&
      currentPatient.insurance?.isModified
    ) {
      // 'both'
      setIsModified(currentPatientIndex, 'both', false);
    } else if (currentPatient?.appointmentType?.isModified) {
      //  'appointment'
      setIsModified(currentPatientIndex, 'appointment', false);
    } else {
      //  : 'insurance',
      setIsModified(currentPatientIndex, 'insurance', false);
    }
  }, [currentPatient]);

  const handleEnableButton = useCallback(
    (enable: boolean) => {
      setListEnableButton(prev => {
        if (
          typeof prev[currentPatientIndex] !== 'undefined' &&
          prev[currentPatientIndex] !== enable
        ) {
          const newVal = [...prev];
          newVal[currentPatientIndex] = enable;
          return newVal;
        } else {
          return prev;
        }
      });
    },
    [currentPatientIndex, setListEnableButton],
  );

  const handleInsuranceEnableButton = useCallback(
    (index: number, enable: boolean) => {
      setInsuranceEnableButtonList(prev => {
        if (typeof prev[index] !== 'undefined' && prev[index] !== enable) {
          const newVal = [...prev];
          newVal[index] = enable;
          return newVal;
        }
        return prev;
      });
    },
    [],
  );

  useEffect(() => {
    const unsubscribe = subscribe<IScheduleExamContinueAttemptEvent>(
      OBEvents.schedContinueAttempt,
      () => {
        setScheduleExamSubmitAttemptCount(count => count + 1);
      },
      {
        self: true,
      },
    );

    return () => unsubscribe();
  }, [subscribe]);

  const handleInsuranceDraftChange = useCallback(
    (index: number, formData?: CalendarInsuranceFormData) => {
      const patient = patients[index];
      const normalizedFormData = normalizeCalendarInsuranceFormData(
        formData,
        insurances,
      );

      setCalendarInsuranceValues(prev => {
        const newVal = [...prev];
        newVal[index] = normalizedFormData?.insurance;
        return newVal;
      });

      if (
        !showInsuranceInSchedule ||
        !patient ||
        !normalizedFormData?.insurance
      ) {
        return;
      }

      const newFormData = { ...normalizedFormData } as {
        insurance: InsuranceInfo;
      };

      newFormData.insurance = {
        ...newFormData.insurance,
        isModified:
          !!patient.scheduleExam &&
          !!patient.insurance &&
          (patient.insurance.id !== newFormData.insurance.id ||
            patient.insurance.other !== newFormData.insurance.other),
      };

      if (!deepEqual(newFormData.insurance, patient.insurance || {})) {
        updatePatient(index, FormIds.Insurance, newFormData);
      }

      if (!patient.scheduleExam) {
        return;
      }

      const insuranceChanged =
        patient.insurance?.plan !== normalizedFormData.insurance.plan ||
        patient.insurance?.id !== normalizedFormData.insurance.id ||
        patient.insurance?.other !== normalizedFormData.insurance.other;

      if (!insuranceChanged) {
        return;
      }

      console.log('## Insurance Change - Event EMIT ##');
      emit<IChangeEvent>(OBEvents.apptChange, {
        type: 'insurance',
      });

      if (index === currentPatientIndex) {
        setReservationStatus(STATUS_ALERT.NEED_TO_DELETE);
      }
    },
    [
      currentPatientIndex,
      emit,
      insurances,
      patients,
      showInsuranceInSchedule,
      updatePatient,
    ],
  );

  const handleInsuranceChange = useCallback(
    (index: number, formData?: CalendarInsuranceFormData) => {
      const patient = patients[index];
      const normalizedFormData = normalizeCalendarInsuranceFormData(
        formData,
        insurances,
      );
      console.log({
        showInsuranceInSchedule,
        patient,
        normalizedFormData,
      });
      if (
        !showInsuranceInSchedule ||
        !patient ||
        !normalizedFormData?.insurance
      ) {
        return;
      }

      const newFormData = { ...normalizedFormData } as {
        insurance: InsuranceInfo;
      };

      newFormData.insurance = {
        ...newFormData.insurance,
        isModified:
          !!patient.scheduleExam &&
          !!patient.insurance &&
          (patient.insurance.id !== newFormData.insurance.id ||
            patient.insurance.other !== newFormData.insurance.other),
      };

      if (deepEqual(newFormData.insurance, patient.insurance || {})) {
        return;
      }

      updatePatient(index, FormIds.Insurance, newFormData);

      if (
        patient.extraInfoScheduleExam?.chooseNewSlot &&
        newFormData.insurance.id === ''
      ) {
        setChooseNewSlot(index, undefined);
      }

      if (index === currentPatientIndex && patient.scheduleExam) {
        setReservationStatus(STATUS_ALERT.NEED_TO_DELETE);
      }
    },
    [
      currentPatientIndex,
      insurances,
      patients,
      setChooseNewSlot,
      showInsuranceInSchedule,
      updatePatient,
    ],
  );
  //#endregion util functions
  const addSlotsOfPatients = (tempSlots: Array<Slot>) => {
    patients.forEach((p, i) => {
      if (
        p &&
        currentPatientIndex !== i &&
        p.scheduleExam &&
        p.scheduleExam.slotId.startTime &&
        format(new Date(p.scheduleExam.slotId.startTime), 'YYYY-MM-DD') ===
          format(selectedDate!, 'YYYY-MM-DD') &&
        p.extraInfoScheduleExam &&
        typeof p.extraInfoScheduleExam.timeTab === 'number'
      ) {
        console.log(
          '#Calendar | use case multiple patient, add slot at user: ',
          i,
        );
        const slot = cloneObject(p.scheduleExam.slotId);
        const slotIndex = tempSlots[
          p.extraInfoScheduleExam.timeTab - 1
        ].list.findIndex(
          sl =>
            sl.doctorId === slot.doctorId &&
            sl.doctorName === slot.doctorName &&
            sl.startTime === slot.startTime,
        );
        if (slotIndex === -1) {
          slot.patientName = `${p.patientDetails?.firstName ? p.patientDetails.firstName + ' ' + p.patientDetails.lastName : 'New patient'}`;
          slot.disabled = true;
          tempSlots[p.extraInfoScheduleExam.timeTab - 1]?.list.push(slot);
        } else if (
          tempSlots[p.extraInfoScheduleExam.timeTab - 1]?.list[slotIndex]
        ) {
          tempSlots[p.extraInfoScheduleExam.timeTab - 1].list[
            slotIndex
          ].patientName =
            `${p.patientDetails?.firstName ? p.patientDetails.firstName + ' ' + p.patientDetails.lastName : 'New patient'}`;
          tempSlots[p.extraInfoScheduleExam.timeTab - 1].list[
            slotIndex
          ].disabled = true;
        }

        // order the list
      }
    });
  };

  //In reschedule flow,this function add the previous slot
  // in the list of slot to show it disabled
  const addPreviousSlotToDisable = useCallback(
    (tempSlots: Array<Slot>) => {
      // no previous slot to disable or no selected date
      if (!previousSlotToDisable || !selectedDate) return;

      // return if we are not in the same day
      const prevDay = previousSlotToDisable.day;
      if (format(selectedDate, 'YYYY-MM-DD') !== prevDay) return;

      // fake disabled slot to add
      const { doctor, day, hour } = previousSlotToDisable;
      const { isoLikeDate: previousSlotDate, time24 } = buildISOLikeDate(
        day,
        hour,
      );

      const prevSlot: HourSlot = {
        slotId: `previous-slot-${doctor}-${previousSlotDate}`,
        doctorId: '',
        doctorName: doctor,
        startTime: previousSlotDate,
        internalSlots: [],
        disabled: true,
        endTime: '',
        telehealth: false,
        slotNumber: 0,
      };

      // --------- (3 tab) ----------
      // Tab 1: 08:00 - 11:59
      // Tab 2: 12:00 - 15:59
      // Tab 3: 16:00 - 20:00
      const TAB_INTERVALS: Array<{ start: string; end: string }> = [
        { start: '08:00', end: '11:59' },
        { start: '12:00', end: '15:59' },
        { start: '16:00', end: '20:00' },
      ];

      const toMinutes = (hhmm: string) => {
        const [hh, mm] = hhmm.split(':').map(s => parseInt(s, 10));
        return (hh || 0) * 60 + (mm || 0);
      };

      const prevMinutes = toMinutes(time24);
      // Find target tab index where the previousslot falls into
      let targetIndex = TAB_INTERVALS.findIndex(interval => {
        const startM = toMinutes(interval.start);
        const endM = toMinutes(interval.end);
        return prevMinutes >= startM && prevMinutes <= endM;
      });

      // If tab not found or out of range, put in the first tab
      if (targetIndex < 0 || targetIndex >= tempSlots.length) targetIndex = 0;

      // put the previous slot in the target tab if not already present
      const slotGroup = tempSlots[targetIndex];

      const alreadyExists = slotGroup.list.some(
        s =>
          s.startTime === prevSlot.startTime &&
          s.doctorName === prevSlot.doctorName,
      );

      if (!alreadyExists) {
        slotGroup.list.push(prevSlot);
      } else {
        console.log(
          '#Calendar | Previous slot already present, skipping insertion',
        );
      }
    },
    [previousSlotToDisable, selectedDate, timeTab, splitTimeSlot],
  );

  //#region logical functions
  useEffect(() => {
    if (currentPatientIndex !== preCurrentPatientIndex.current) {
      console.log('#Calendar | use case user changed start flow');

      const currPatientInternal = getCurrentPatient(currentPatientIndex);
      const prePatientInternal = getCurrentPatient(
        preCurrentPatientIndex.current,
      );
      if (
        currPatientInternal &&
        (currPatientInternal.appointmentType?.isModified ||
          currPatientInternal.insurance?.isModified) &&
        reservationStatus === STATUS_ALERT.NO_ACTION
      ) {
        console.log(
          '#Calendar | use case user changed appointment type or insurance',
        );
        setReservationStatus(STATUS_ALERT.NEED_TO_DELETE);
      } else {
        console.log(
          '#Calendar | use case user NOT changed appointment type or insurance',
        );
        setReservationStatus(STATUS_ALERT.HAPPY_FLOW);
      }

      if (currPatientInternal) {
        console.log('#Calendar | new currentPatient');
        setSelectedHourSlot(currPatientInternal.scheduleExam?.slotId);
        setTimeTab(currPatientInternal.extraInfoScheduleExam?.timeTab);
        setSelectedDate(currPatientInternal.scheduleExam?.day);
        if (
          preCurrentPatientIndex.current !== -1 &&
          currPatientInternal?.appointmentType.appointmentType ===
            prePatientInternal?.appointmentType.appointmentType &&
          (currPatientInternal.insurance?.id ===
            prePatientInternal?.insurance?.id ||
            (currPatientInternal.insurance?.name ===
              prePatientInternal?.insurance?.name &&
              currPatientInternal.insurance?.name !== ''))
        ) {
          console.log(
            '#Calendar | NEW PATIENT | use case user changed with same insurance and appType',
            slots,
          );
          const tempSlots = createHourSlotGroups(slots);
          if (
            currPatientInternal.extraInfoScheduleExam?.timeTab === undefined &&
            !selectedHourSlot
          ) {
            console.log(
              '#Calendar | NEW PATIENT | use case slots #1: first time in this page: ',
              tempSlots,
            );
            // set timeTab automatically
            switch (true) {
              case tempSlots[0].list.length > 0:
                console.log(
                  '#setTimeTab set automatically: ',
                  tempSlots[0].timeSlotOrder,
                );
                setTimeTab(tempSlots[0].timeSlotOrder);
                break;
              case tempSlots[1].list.length > 0:
                console.log(
                  '#setTimeTab set automatically: ',
                  tempSlots[1].timeSlotOrder,
                );
                setTimeTab(tempSlots[1].timeSlotOrder);
                break;
              case tempSlots[2].list.length > 0:
                console.log(
                  '#setTimeTab set automatically: ',
                  tempSlots[2].timeSlotOrder,
                );
                setTimeTab(tempSlots[2].timeSlotOrder);
                break;
              default:
                console.log(
                  '#setTimeTab set automatically: ',
                  tempSlots[0].timeSlotOrder,
                );
                setTimeTab(tempSlots[0].timeSlotOrder);
            }
            // set all Slots
            addSlotsOfPatients(tempSlots);
            setHourSlots(tempSlots);
          } else if (
            reservationStatus === STATUS_ALERT.HAPPY_FLOW &&
            currPatientInternal.scheduleExam?.slotId &&
            format(
              new Date(currPatientInternal.scheduleExam?.slotId.startTime),
              'YYYY-MM-DD',
            ) === format(selectedDate!, 'YYYY-MM-DD') &&
            !!currPatientInternal.extraInfoScheduleExam?.timeTab
          ) {
            console.log(
              '#Calendar | NEW PATIENT | use case slots #2: user selected a slot and return in this page',
            );
            console.log(
              '#Calendar | NEW PATIENT | pre slot index tempSlots: ',
              tempSlots,
            );
            const selectedPatientSlot = currPatientInternal.scheduleExam.slotId;
            // The stored timeTab can be stale here; resolve the tab from the selected slot time.
            const selectedPatientTimeTabIndex =
              getTimeTabIndexBySlotStartTime(selectedPatientSlot);
            addSelectedSlotToTimeTab(
              tempSlots,
              selectedPatientSlot,
              selectedPatientTimeTabIndex,
            );
            console.log('#Calendar | NEW PATIENT | pre tempSlots: ', [
              ...tempSlots,
            ]);
            addSlotsOfPatients(tempSlots);
            console.log('#Calendar | NEW PATIENT | tempSlots: ', [
              ...tempSlots,
            ]);

            setHourSlots(tempSlots);
            handleEnableButton(true);
          }
        }
      }
      preCurrentPatientIndex.current = currentPatientIndex;
    }
  }, [currentPatient, reservationStatus]);

  useEffect(() => {
    console.log('#Calendar | reservationStatus: ', reservationStatus);
    if (reservationStatus === STATUS_ALERT.NEED_TO_DELETE) {
      console.log('#Calendar | useEffect reservationStatus delete reservation');
      deleteReservationMutate(reservationId).catch(() => {});
    } else if (
      reservationStatus === STATUS_ALERT.SLOT_NOT_AVAILABLE ||
      reservationStatus === STATUS_ALERT.DAY_NOT_AVAILABLE
    ) {
      console.log('#Calendar | useEffect reservationStatus reset ...');
      const type =
        currentPatient &&
        currentPatient.appointmentType?.isModified &&
        currentPatient.insurance?.isModified
          ? ChooseNewSlotType.BOTH
          : currentPatient?.appointmentType?.isModified
            ? ChooseNewSlotType.APPOINTMENT
            : ChooseNewSlotType.INSURANCE;
      setChooseNewSlot(currentPatientIndex, {
        type,
      });
      setSelectedHourSlot(undefined); // reset slot
      resetScheduleExam(currentPatientIndex);
      handleEnableButton(false);
      resetIsModified();
      if (reservationStatus === STATUS_ALERT.DAY_NOT_AVAILABLE) {
        console.log('#Calendar | useEffect reservationStatus reset ... all');
        setHourSlots(undefined); // reset slots
        // setSelectedDate(undefined); // reset day
        setTimeSlotOrder(currentPatientIndex, undefined);
        setTimeTab(undefined);
      }
      setReservationStatus(STATUS_ALERT.HAPPY_FLOW);
    }
  }, [reservationStatus]);

  useEffect(() => {
    if (
      reservationStatus === STATUS_ALERT.NEED_TO_DELETE &&
      (deleteReservationIsSuccess || deleteReservationIsError)
    ) {
      console.log(
        '#Calendar | useEffect deleteReservation delete reservationId in store and reset timer',
      );
      setStoreReservationId(currentPatientIndex, undefined);
      setTimer(currentPatientIndex, undefined);
      setReservationStatus(STATUS_ALERT.GET_DAYS_TO_VERIFY);
    }
  }, [deleteReservationIsError, deleteReservationIsSuccess, reservationStatus]);

  useEffect(() => {
    console.log(
      '#Calendar | useEffect slots: ',
      !!(slots !== undefined && !getSlotsIsLoading),
    );
    if (slots && !getSlotsIsLoading) {
      // tempSlots contains all slots also empty
      const tempSlots = createHourSlotGroups(slots);
      // add previous slot to disable if in reschedule flow
      addPreviousSlotToDisable(tempSlots);
      console.log('#Calendar | timeTab: ', timeTab);
      console.log('#Calendar | selectedHourSlot: ', selectedHourSlot);
      if (timeTab === undefined && !selectedHourSlot) {
        console.log(
          '#Calendar | use case slots #1: first time in this page: ',
          tempSlots,
        );
        // set timeTab automatically
        switch (true) {
          case tempSlots[0].list.length > 0:
            console.log(
              '#setTimeTab set automatically: ',
              tempSlots[0].timeSlotOrder,
            );
            setTimeTab(tempSlots[0].timeSlotOrder);
            break;
          case tempSlots[1].list.length > 0:
            console.log(
              '#setTimeTab set automatically: ',
              tempSlots[1].timeSlotOrder,
            );
            setTimeTab(tempSlots[1].timeSlotOrder);
            break;
          case tempSlots[2].list.length > 0:
            console.log(
              '#setTimeTab set automatically: ',
              tempSlots[2].timeSlotOrder,
            );
            setTimeTab(tempSlots[2].timeSlotOrder);
            break;
          default:
            console.log(
              '#setTimeTab set automatically: ',
              tempSlots[0].timeSlotOrder,
            );
            setTimeTab(tempSlots[0].timeSlotOrder);
        }
        // set all Slots
        addSlotsOfPatients(tempSlots);
        setHourSlots(tempSlots);
      } else if (
        reservationStatus === STATUS_ALERT.HAPPY_FLOW &&
        selectedHourSlot &&
        format(new Date(selectedHourSlot.startTime), 'YYYY-MM-DD') ===
          format(selectedDate!, 'YYYY-MM-DD') &&
        !!timeTab
      ) {
        console.log(
          '#Calendar | use case slots #2: user selected a slot and return in this page',
        );

        // The active tab is preserved; only the selected slot placement is corrected.
        const selectedSlotTimeTabIndex =
          getTimeTabIndexBySlotStartTime(selectedHourSlot);
        addSelectedSlotToTimeTab(
          tempSlots,
          selectedHourSlot,
          selectedSlotTimeTabIndex,
        );

        addSlotsOfPatients(tempSlots);
        console.log('#Calendar | tempSlots: ', tempSlots);
        setHourSlots(tempSlots);
        handleEnableButton(true);
      } else if (
        reservationStatus === STATUS_ALERT.DAY_AVAILABLE &&
        selectedHourSlot &&
        timeTab !== undefined
      ) {
        const slotIndex = tempSlots[timeTab - 1].list.findIndex(slot =>
          isSameHourSlot(slot, selectedHourSlot),
        );
        console.log(
          '#Calendar | use case slots #3: user changed appointment or insurance and the day is available then we need to check if slot is available: ',
          !!(slotIndex !== -1),
        );
        if (slotIndex !== -1) {
          const nuovoSlot = tempSlots[timeTab - 1].list[slotIndex];
          setReservationStatus(STATUS_ALERT.SLOT_AVAILABLE);
          onTimeSlotClick(nuovoSlot);
        } else {
          setReservationStatus(STATUS_ALERT.SLOT_NOT_AVAILABLE);
        }
        addSlotsOfPatients(tempSlots);
        setHourSlots(tempSlots);
      } else {
        console.log('#Calendar | use case user change the day selected');
        // set all Slots
        addSlotsOfPatients(tempSlots);
        setHourSlots(tempSlots);
      }
    }
  }, [slots, getSlotsIsLoading, addPreviousSlotToDisable]);

  // effect to recalculate patient slot's timeTab if selectedHourSlot change
  useEffect(() => {
    if (slots && selectedHourSlot) {
      console.log(
        '#Calendar | useEffect | Recalculation of timeTab based on selectedHourSlot and slots',
      );
      setTimeTabFromSelectedSlot(slots, selectedHourSlot);
    }
  }, [selectedHourSlot]);

  // hookCustom for save information
  useEffect(() => {
    if (
      isCreateReservationSuccess &&
      !isCreateReservationLoading &&
      selectedDate &&
      reservationData
    ) {
      console.log(
        '#Calendar | useEffect reservation response: ',
        selectedDate,
        selectedHourSlot,
        currentPatientIndex,
      );
      const toSave = {
        scheduleExam: {
          day: format(selectedDate, 'YYYY-MM-DD'),
          hour: format(
            new Date(selectedHourSlot?.startTime || ''),
            HOURTEMPLATE,
          ),
          doctor: selectedHourSlot?.doctorName,
          slotId: selectedHourSlot,
        },
      };
      updatePatient(currentPatientIndex, FormIds.ScheduleExam, toSave);
      setTimeSlotOrder(currentPatientIndex, timeTab);
      handleEnableButton(true);
      setStoreReservationId(currentPatientIndex, reservationData.reservationId);
      setTimer(currentPatientIndex, reservationData.timer);
      resetIsModified();
      setChooseNewSlot(currentPatientIndex, undefined);
    } else if (isError && !isCreateReservationLoading) {
      console.log(
        '#Calendar | useEffect reservation error setEnableButton to false',
      );
      handleEnableButton(false);
    }
  }, [
    isCreateReservationSuccess,
    isCreateReservationLoading,
    isError,
    reservationData,
  ]);

  useEffect(() => {
    if (isError) {
      setShowSlotAlert(true);
    } else {
      setShowSlotAlert(false);
    }
  }, [isError, selectedHourSlot]);

  useEffect(() => {
    if (selectedDate && showSlotAlert) {
      setShowSlotAlert(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    console.log(
      '#Calendar | useEffect enableButtonMaster: ',
      enableButtonMaster,
    );
    setEnableButton(enableButtonMaster);
  }, [enableButtonMaster]);

  useEffect(() => {
    if (enableButtonMaster) {
      setScheduleExamSubmitAttemptCount(0);
    }
  }, [enableButtonMaster]);

  useEffect(() => {
    console.log('#listEnableButton: ', listEnableButton);
    const patientsToCheck: string[] = listEnableButton.reduce(
      (acc, isEnabled, index) => {
        if (
          isEnabled !== undefined &&
          !isEnabled &&
          index !== currentPatientIndex &&
          patients[index]
        ) {
          const patient = patients[index].patientDetails;
          acc.push(
            `${patient?.firstName ?? 'New'} ${patient?.lastName ?? 'Patient'}`,
          );
        }
        return acc;
      },
      new Array<string>(),
    );

    console.log('#patientsToCheck: ', patientsToCheck);
    emit<ICheckEvent>(OBEvents.schedCheck, {
      patients: patientsToCheck,
    });
  }, [listEnableButton]);

  const renderScheduleInsuranceContent = () => {
    if (isInsurancesLoading) {
      return <LuxLoader width="100%" />;
    }

    if (isErrorInInsurances) {
      return (
        <LuxTypography variant="textMdRegular" padding="1rem 1.5rem">
          {t('error')}
        </LuxTypography>
      );
    }

    return patients.map((patient, index) =>
      patient ? (
        <CalendarInsuranceForm
          key={`scheduleInsurance_${patient.patientId || patient.order}`}
          currentIndex={index}
          insurances={insurances || []}
          initialInsurance={patient.insurance}
          isVisible={currentPatientIndex === index}
          submitAttemptCount={
            currentPatientIndex === index ? scheduleExamSubmitAttemptCount : 0
          }
          onValidate={enable => handleInsuranceEnableButton(index, enable)}
          onChange={formData => handleInsuranceDraftChange(index, formData)}
          onCommit={formData => handleInsuranceChange(index, formData)}
        />
      ) : null,
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_schedule_exam_title')}</title>
      </Helmet>
      {showInsuranceInSchedule && (
        <StepContainer
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        >
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4" component="h2">
              {t('detailsSection.insurance')}
            </LuxTypography>
          </CardTitleWrapper>
          <MultiplePatientsTabs
            onSelect={() => setReservationStatus(STATUS_ALERT.NO_ACTION)}
          />
          {renderScheduleInsuranceContent()}
        </StepContainer>
      )}
      <StepContainer
        borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        marginTop={showInsuranceInSchedule ? '1rem' : undefined}
      >
        <CardTitleWrapper
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        >
          <LuxTypography variant="heading4" component="h2">
            {t('calendarPage.title')}
          </LuxTypography>
        </CardTitleWrapper>
        {!showInsuranceInSchedule && (
          <MultiplePatientsTabs
            onSelect={() => setReservationStatus(STATUS_ALERT.NO_ACTION)}
          />
        )}
        <CalendarComponent
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          reservationStatus={reservationStatus}
          setReservationStatus={setReservationStatus}
          slotsAreLoading={getSlotsIsLoading}
          useExternalInsuranceSelectedValue={showInsuranceInSchedule}
          insuranceSelectedValue={insuranceSelected}
        />
      </StepContainer>
      <StepContainer
        borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        marginTop="1rem"
      >
        <CardTitleWrapper
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
          id="hour-slots-container"
          style={{ scrollMarginTop: isMobile ? 200 : 160 }}
        >
          <LuxTypography variant="heading4" component="h2">
            {showDoctors
              ? t('calendarPage.titleHourSlotsWithDoctors')
              : t('calendarPage.titleHourSlots')}
          </LuxTypography>
          {showDoctors && (
            <LuxButton
              iconStart="DoctorIcon"
              variant="ghost"
              size="small"
              onClick={() => setIsMoreInfoOpen(true)}
              style={{ textDecoration: brandName && 'underline' }}
            >
              <LuxTypography
                variant="textMedium"
                fontWeight={brandName && '700!important'}
              >
                {t('calendarPage.discoverDoctors')}
              </LuxTypography>
            </LuxButton>
          )}
        </CardTitleWrapper>
        <CalendarSection>
          {showRequiredSlotError && (
            <LuxTypography
              variant="textXs"
              style={{
                color: theme.palette.LUX.obTokens.textColorFunctionalError,
              }}
            >
              {t('calendarPage.selectASlot')}
            </LuxTypography>
          )}
          {!hasQueriedSlots || getSlotsIsLoading ? (
            <LuxLoader width="100%" />
          ) : !hourSlots && hasQueriedSlots ? (
            <LuxTypography
              variant="heading4"
              align="center"
              component="div"
              width="100%"
            >
              {t('calendarPage.notAvailableDays')}
            </LuxTypography>
          ) : (
            <>
              {splitTimeSlot && (
                <LuxTabsGroup
                  tabs={hourSlices}
                  onSelect={index => {
                    setTimeTab((index as number) + 1);
                  }}
                  showIcon={false}
                  selected={timeTab !== undefined ? timeTab - 1 : 0}
                />
              )}
              <div style={{ position: 'relative', width: '100%' }}>
                {isLoadingSlot && (
                  <CalendarOverlay
                    container
                    columnSpacing={{ xs: 1, sm: 2 }}
                    rowSpacing={{ xs: 1, sm: 2 }}
                  >
                    <LuxLoader />
                  </CalendarOverlay>
                )}

                <Grid
                  container
                  columnSpacing={{ xs: 1, sm: 2 }}
                  rowSpacing={{ xs: 1, sm: 2 }}
                >
                  {hourSlots &&
                    (!splitTimeSlot
                      ? hourSlots.flatMap((slotGroup, groupIdx) =>
                          sortHourSlots(slotGroup.list).map((slot, index) => {
                            const isCurrent =
                              selectedHourSlot &&
                              isSameHourSlot(slot, selectedHourSlot);
                            const status = getHourSlotStatus(slot);
                            const isDisabled =
                              status === 'disabled' ||
                              isPreviousSlotToDisable(slot);
                            const selectionLabel = isPreviousSlotToDisable(slot)
                              ? t('calendarPage.previousSelection')
                              : inRescheduleFlow && isCurrent
                                ? t('calendarPage.newSelection')
                                : slot.patientName
                                  ? slot.patientName
                                  : isError
                                    ? undefined
                                    : (isCurrent &&
                                        showYourSelection &&
                                        t(
                                          'appointmentTypePage.yourSelection',
                                        )) ||
                                      '';
                            return (
                              <Grid
                                item
                                xs={12 / numColumnsTimeSlot}
                                md={3}
                                key={`CalendarPageHourSlot-${groupIdx}-${index}`}
                                sx={{
                                  '& .MuiButtonBase-root': {
                                    height: '5.125rem',
                                    width: '100%',
                                    padding: '0.5rem 1rem',
                                    '@media screen and (max-width: 576px)': {
                                      height: '4.75rem',
                                      padding: '0.5rem',
                                    },
                                  },
                                }}
                              >
                                <LuxTimeSlot
                                  variant="completeVariant"
                                  reverseVw
                                  selectionLabelOnTop
                                  onClick={() =>
                                    !isSameHourSlot(slot, selectedHourSlot)
                                      ? onTimeSlotClick(slot)
                                      : null
                                  }
                                  timeSlotValue={{
                                    date: new Date(slot.startTime),
                                    templateDate: HOURTEMPLATE,
                                    sublabel:
                                      showDoctors && slot.doctorName !== '-'
                                        ? slot.doctorName
                                        : undefined,
                                    status,
                                    isDisabled,
                                    showIcons:
                                      showDoctors && slot.doctorName !== '-',
                                    selectionLabel: selectionLabel,
                                    isRemoteDoctor:
                                      (slot.isRemoteDoctor &&
                                        brandName === BrandNameList.TO) ||
                                      false,
                                  }}
                                />
                              </Grid>
                            );
                          }),
                        )
                      : timeTab !== undefined &&
                          hourSlots[timeTab - 1].list?.length > 0
                        ? hourSlots[timeTab - 1]?.list.map((slot, index) => {
                            const isCurrent =
                              selectedHourSlot &&
                              isSameHourSlot(slot, selectedHourSlot);

                            const status = getHourSlotStatus(slot);
                            const isDisabled =
                              status === 'disabled' ||
                              isPreviousSlotToDisable(slot);
                            const selectionLabel = isPreviousSlotToDisable(slot)
                              ? t('calendarPage.previousSelection')
                              : inRescheduleFlow && isCurrent
                                ? t('calendarPage.newSelection')
                                : slot.patientName
                                  ? slot.patientName
                                  : isError
                                    ? undefined
                                    : (isCurrent &&
                                        showYourSelection &&
                                        t(
                                          'appointmentTypePage.yourSelection',
                                        )) ||
                                      '';
                            return (
                              <Grid
                                item
                                xs={12 / numColumnsTimeSlot}
                                md={3}
                                key={`CalendarPageHourSlot-${index}`}
                                sx={{
                                  '& .MuiButtonBase-root': {
                                    height: '5.125rem',
                                    width: '100%',
                                    padding: '0.5rem 1rem',
                                    '@media screen and (max-width: 576px)': {
                                      height: '4.75rem',
                                      padding: '0.5rem',
                                    },
                                  },
                                }}
                              >
                                <LuxTimeSlot
                                  variant="completeVariant"
                                  reverseVw
                                  selectionLabelOnTop
                                  onClick={() =>
                                    !isSameHourSlot(slot, selectedHourSlot)
                                      ? onTimeSlotClick(slot)
                                      : null
                                  }
                                  timeSlotValue={{
                                    date: new Date(slot.startTime),
                                    templateDate: HOURTEMPLATE,
                                    sublabel:
                                      showDoctors && slot.doctorName !== '-'
                                        ? slot.doctorName
                                        : undefined,
                                    status,
                                    isDisabled,
                                    showIcons:
                                      showDoctors && slot.doctorName !== '-',
                                    selectionLabel: selectionLabel,
                                  }}
                                />
                              </Grid>
                            );
                          })
                        : timeTab !== undefined && (
                            <LuxTypography
                              variant="heading4"
                              align="center"
                              marginTop="1rem"
                              width={'100%'}
                            >
                              {t('calendarPage.noHourSlotsError').replace(
                                '{timeBandName}',
                                timeSlotLabels[timeTab - 1],
                              )}
                            </LuxTypography>
                          ))}
                </Grid>
                <MultipatientsTags showHour />
                {/*Pill to show eventual previous selection */}
                {previousSlotToDisable && isMobile && (
                  <LuxSelectablePill
                    key={`calendar-previous-selection-time-slot-pill`}
                    selectable={false}
                    variant="pill"
                    text={
                      <>
                        <LuxTypography fontWeight={700} variant="textMdRegular">
                          {t('calendarPage.previousSelection')}:
                        </LuxTypography>
                        <LuxTypography fontWeight={400} variant="textMdRegular">
                          {previousSlotToDisable.hour}
                        </LuxTypography>
                      </>
                    }
                  />
                )}
              </div>
              {brandName === BrandNameList.TO &&
                selectedHourSlot?.isRemoteDoctor && (
                  <LuxSelectablePill
                    variant="pill"
                    color="info"
                    selectable={false}
                    text={
                      <LuxTypography
                        fontSize={theme.palette.LUX.obTokens.textSmSize}
                        fontFamily={theme.palette.LUX.obTokens.textSmFamily}
                        style={{ whiteSpace: 'normal' }}
                      >
                        {t('calendarPage.remoteDoctorPillMessage')}
                      </LuxTypography>
                    }
                  />
                )}
              {/*show this pill only for RONA*/}
              {generalData?.phoneNumber && !selectedHourSlot && brandName && (
                <LuxSelectablePill
                  variant="pill"
                  color="info"
                  selectable={false}
                  text={callUsClickable}
                />
              )}
              {generalConfig?.locationInformation?.showCalendarCustomMessage &&
                generalConfig?.locationInformation?.calendarCustomMessage &&
                (generalConfig?.customization
                  ?.toastTypeCalendarCustomMessage === 'info' ? (
                  <LuxSelectablePill
                    variant="pill"
                    color="info"
                    selectable={false}
                    text={t(
                      generalConfig.locationInformation.calendarCustomMessage,
                    )}
                  />
                ) : (
                  <LuxTypography variant="textMdRegular">
                    {t(generalConfig.locationInformation.calendarCustomMessage)}
                  </LuxTypography>
                ))}
              {showSlotAlert && (
                <LuxSelectablePill
                  variant="pill"
                  color="error"
                  selectable={false}
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                  text={t('calendarPage.hourSlotError') || ''}
                  styleWrapLuxTypography={{
                    display: 'inline-grid',
                  }}
                />
              )}
              <MultiplePatientsNavigator noPadding />
            </>
          )}
        </CalendarSection>
      </StepContainer>
      <MoreInformationModal
        open={isMoreInfoOpen}
        onClose={() => setIsMoreInfoOpen(false)}
        generalData={generalData}
        fromViewDoctors
      />
    </>
  );
};

export default CalendarPage;
